const VALID_ACCESS_MODES = new Set(["free", "trial"]);
export const INTERVIEW_CONFIG_STORAGE_KEY = "interviewConfig";
const VALID_INTERVIEW_TYPES = new Set(["behavioral", "technical", "mixed"]);
export const INTERVIEW_TYPES_REQUIRING_SKILLS = new Set(["technical", "mixed"]);
const DEFAULT_DURATION = 10;
const VALID_DURATIONS = new Set([10, 15, 20, 30, 45, 60]);
const VALID_DIFFICULTIES = new Set(["auto", "easy", "medium", "hard"]);
const VALID_QUESTION_MIX = new Set(["technical", "balanced", "behavioral"]);
const VALID_INTERVIEW_STYLES = new Set(["friendly", "neutral", "strict"]);
const VALID_INTERVIEW_FORMATS = new Set(["phone_screen", "technical", "system_design", "behavioral", "case", "take_home"]);
const VALID_JOB_LEVELS = new Set(["student", "early_career", "mid", "senior"]);
const LEGACY_JOB_LEVELS = {
  intern: "student",
  junior: "early_career",
  lead: "senior",
};

export function getInterviewAccessMode() {
  const raw = String(process.env.REACT_APP_INTERVIEW_ACCESS_MODE || "free").trim().toLowerCase();
  return VALID_ACCESS_MODES.has(raw) ? raw : "free";
}

export function isInterviewFreeAccessMode() {
  return getInterviewAccessMode() === "free";
}

function normalizeString(value) {
  const next = String(value || "").trim();
  return next || undefined;
}

function normalizeSelectedSkills(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => normalizeString(entry))
    .filter(Boolean);
}

function normalizeJobLevel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return undefined;
  if (VALID_JOB_LEVELS.has(normalized)) return normalized;
  return LEGACY_JOB_LEVELS[normalized] || "mid";
}

export function buildInterviewConfig(input = {}) {
  const type = VALID_INTERVIEW_TYPES.has(String(input.type || "").trim().toLowerCase())
    ? String(input.type).trim().toLowerCase()
    : "technical";
  const rawDuration = Number(input.duration);
  const duration = VALID_DURATIONS.has(rawDuration) ? rawDuration : DEFAULT_DURATION;
  const difficulty = VALID_DIFFICULTIES.has(String(input.difficulty || "").trim().toLowerCase())
    ? String(input.difficulty).trim().toLowerCase()
    : "auto";
  const questionMix = VALID_QUESTION_MIX.has(String(input.questionMix || "").trim().toLowerCase())
    ? String(input.questionMix).trim().toLowerCase()
    : "balanced";
  const interviewStyle = VALID_INTERVIEW_STYLES.has(String(input.interviewStyle || "").trim().toLowerCase())
    ? String(input.interviewStyle).trim().toLowerCase()
    : "neutral";
  const targetInterviewFormat = VALID_INTERVIEW_FORMATS.has(String(input.targetInterviewFormat || "").trim().toLowerCase())
    ? String(input.targetInterviewFormat).trim().toLowerCase()
    : undefined;
  const selectedSkills = normalizeSelectedSkills(input.selectedSkills);
  const accessMode = VALID_ACCESS_MODES.has(String(input.accessMode || "").trim().toLowerCase())
    ? String(input.accessMode).trim().toLowerCase()
    : getInterviewAccessMode();

  return {
    type,
    duration,
    difficulty,
    role: normalizeString(input.role),
    company: normalizeString(input.company),
    jobLevel: normalizeJobLevel(input.jobLevel),
    yearsExperience: Number.isFinite(Number(input.yearsExperience)) ? Number(input.yearsExperience) : undefined,
    targetJobDescription: normalizeString(input.targetJobDescription),
    targetJobUrl: normalizeString(input.targetJobUrl),
    targetInterviewFormat,
    questionMix,
    interviewStyle,
    sessionId: normalizeString(input.sessionId),
    selectedSkills,
    accessMode,
    trialMode: accessMode === "trial",
  };
}

export function validateInterviewConfig(config) {
  if (!config || typeof config !== "object") {
    return { valid: false, reason: "missing_setup" };
  }

  const normalized = buildInterviewConfig(config);
  if (!VALID_INTERVIEW_TYPES.has(normalized.type)) {
    return { valid: false, reason: "invalid_type", config: normalized };
  }
  if (INTERVIEW_TYPES_REQUIRING_SKILLS.has(normalized.type) && !Array.isArray(config.selectedSkills)) {
    return { valid: false, reason: "invalid_skill_selection", config: normalized };
  }
  return { valid: true, config: normalized };
}

export function readSavedInterviewConfig(storage = typeof window !== "undefined" ? window.sessionStorage : null) {
  if (!storage) {
    return { valid: false, reason: "storage_unavailable" };
  }
  const raw = storage.getItem(INTERVIEW_CONFIG_STORAGE_KEY);
  if (!raw) {
    return { valid: false, reason: "missing_setup" };
  }
  try {
    return validateInterviewConfig(JSON.parse(raw));
  } catch {
    return { valid: false, reason: "invalid_json" };
  }
}

export function saveInterviewConfig(config, storage = typeof window !== "undefined" ? window.sessionStorage : null) {
  if (!storage) return null;
  const normalized = buildInterviewConfig(config);
  storage.setItem(INTERVIEW_CONFIG_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}
